import React from 'react';
import { useTranslate, usePermissions } from 'react-admin';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { H2, TextField, StatusField } from '~/components';
import { AvatarField } from '~/components/ra';
import { TreeNode } from '~/types/Menu';
import Section from '~/layout/Section/Section';
import EditAction from './EditAction';
import DeleteAction from './DeleteAction';
import CreateAction from './CreateAction';
import { hasTranslation, Longdash } from '~/utils';

const l = (key: string): string => `resources.menu.fields.${key}`;

interface Props {
  selectedNode: TreeNode | null;
  menuId: string;
  refetch: () => Promise<any>;
  handleResetState: () => void;
}

const DetailsItemDrawer = (props: Props) => {
  const { selectedNode, menuId, refetch, handleResetState } = props;
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));
  const classes = useStyles();
  const { permissions } = usePermissions();

  if (!permissions?.includes('dynamicMenu.view') || !selectedNode) return null;
  return (
    <Section noPaddings>
      <Grid container justify='space-between' className={classes.bottomSpacing2}>
        <Grid container item alignItems='center' xs={12}>
          <AvatarField
            record={selectedNode}
            source='branding.icon'
            colorSource='branding.color'
            resource={`menu/${menuId}/items`}
            noText={true}
            size='50px'
            onSuccess={refetch}
            updatable={
              permissions?.includes('dynamicMenu.update') && selectedNode?.deleted !== true
            }
          />
          <H2 bottomSpacing={0} topSpacing={0} className={classes.header}>
            {t('itemDetails')}
          </H2>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField label={t('id')} ellipsis>
            {selectedNode?.id}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField ellipsis label={t('code')}>
            {selectedNode?.code || Longdash}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField label={t('itemType')}>
            {selectedNode?.itemType && hasTranslation(l(selectedNode?.itemType as string))
              ? t(selectedNode?.itemType)
              : selectedNode?.itemType}
          </TextField>
        </Grid>
        {selectedNode?.itemType !== 'GROUP' && (
          <Grid item xs={12}>
            {selectedNode?.itemType === 'SERVICE' && (
              <TextField label={t('serviceId')}>{selectedNode?.serviceId}</TextField>
            )}
            {selectedNode?.itemType === 'ACTION' && (
              <TextField label={t('actionId')}>{selectedNode?.actionId}</TextField>
            )}
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField ellipsis label={t('priorityIndex')}>
            {selectedNode?.index}
          </TextField>
        </Grid>
        {selectedNode?.fallbackName && (
          <Grid item xs={12}>
            <TextField ellipsis label={t('fallbackName')}>
              {selectedNode?.fallbackName}
            </TextField>
          </Grid>
        )}
        {selectedNode?.fallbackDescription && (
          <Grid item xs={12}>
            <TextField ellipsis multiline rows={5} label={t('fallbackDescription')}>
              {selectedNode?.fallbackDescription}
            </TextField>
          </Grid>
        )}
        {selectedNode?.deleted && (
          <Grid item xs={12}>
            <TextField label={t('status')}>
              <StatusField status='deleted' />
            </TextField>
          </Grid>
        )}
      </Grid>
      {!selectedNode?.deleted && (
        <Grid container justify='flex-end' className={classes.bottomSpacing2} spacing={2}>
          <Grid item container xs={12} id='section-actions' justify='flex-end'>
            {permissions?.includes('dynamicMenu.create') && selectedNode?.itemType === 'GROUP' && (
              <Grid item>
                <CreateAction selectedType='menuItem' menuId={menuId} parentId={selectedNode?.id} />
              </Grid>
            )}
            {permissions?.includes('dynamicMenu.update') && (
              <Grid item>
                <EditAction
                  selectedNode={selectedNode}
                  selectedType='menuItem'
                  menuId={menuId}
                  parentId={selectedNode?.parentId}
                  refetch={refetch}
                />
              </Grid>
            )}
            {permissions?.includes('dynamicMenu.delete') && (
              <Grid item>
                <DeleteAction
                  selectedNode={selectedNode}
                  selectedType='menuItem'
                  menuId={menuId}
                  parentId={selectedNode?.parentId}
                  handleResetState={handleResetState}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
    </Section>
  );
};

const useStyles = makeStyles((theme) => ({
  bottomSpacing2: {
    marginBottom: theme.spacing(2),
  },
  header: {
    marginLeft: theme.spacing(1),
  },
}));

export default DetailsItemDrawer;
